<template>
  <section
    :dir="$i18n.locale === 'en' ? 'ltr' : 'rtl'"
    class="post-moderation ph-no-capture"
  >
    <div class="moderation-heading">
      <div><h2>{{ words.title }}</h2><p>{{ words.subtitle }}</p></div><span class="moderation-count">{{ rows.length }} {{ words.onPage }}</span>
    </div>
    <form
      class="filters"
      @submit.prevent="load(1)"
    >
      <label>{{ words.status
      }}<select v-model="status">
        <option value="">{{ words.all }}</option>
        <option
          v-for="value in statuses"
          :key="value"
          :value="value"
        >{{ words[value] }}</option>
      </select></label>
      <label>{{ words.language
      }}<select v-model="language">
        <option value="">{{ words.allLanguages }}</option>
        <option
          v-for="value in ['fa', 'en', 'ar']"
          :key="value"
          :value="value"
        >
          {{ words['language_' + value] }}
        </option>
      </select></label>
      <label>{{ words.search }}<input
        v-model="search"
        maxlength="100"
        type="search"
      ></label>
      <details class="advanced-filters">
        <summary>{{ words.advancedFilters }} <span v-if="postId">{{ words.activePostFilter }}</span></summary>
        <label>{{ words.postId }}<input
          v-model="postId"
          pattern="[a-fA-F0-9]{24}"
          maxlength="24"
          dir="ltr"
        ></label>
      </details>
      <button
        type="submit"
        :disabled="loading"
      >
        {{ words.load }}
      </button>
      <button
        v-if="rootId"
        type="button"
        @click="
          rootId = ''
          load(1)
        "
      >
        {{ words.clear }}
      </button>
    </form>
    <p
      v-if="message"
      :role="failed ? 'alert' : 'status'"
    >
      {{ message }}
    </p>
    <p
      v-if="loading"
      role="status"
    >
      {{ words.loading }}
    </p>
    <p v-else-if="showEmpty">
      {{ words.empty }}
    </p>
    <article
      v-for="row in rows"
      :key="row['_id']"
      class="moderation-row"
      :class="{ 'official-row': row.authorKind === 'admin' }"
    >
      <header>
        <strong dir="auto">{{ row.authorName }}</strong>
        <span class="author-meta">{{ words[row.authorKind] }} · {{ words['language_' + row.language] }}</span>
        <span
          class="status-badge"
          :class="'status-' + row.status"
        >{{ words[row.status] }}</span><time :datetime="row.createdAt">{{ date(row.createdAt) }}</time>
      </header>
      <p class="post-context">
        {{ postKindLabel(row) }}:
        <a
          v-if="postLink(row)"
          :href="postLink(row)"
          target="_blank"
          rel="noopener noreferrer"
          class="post-link"
          :aria-label="postTitle(row) + ' — ' + words.openPost"
        ><b dir="auto">{{ postTitle(row) }}</b><span class="post-link-hint">{{ words.openPost }} ↗</span></a>
        <template v-else>
          <b dir="auto">{{ postTitle(row) }}</b><small class="post-link-hint">{{ words.postLinkUnavailable }}</small>
        </template>
      </p>
      <details class="post-reference">
        <summary>{{ words.postReference }}</summary><small dir="ltr">{{ row.postId }}</small>
        <bdi
          v-if="row.post && row.post.slug"
          class="post-slug"
          dir="ltr"
        >{{ row.post.slug }}</bdi>
      </details>
      <p v-if="row.replyToName">
        {{ words.replyTo }} <b dir="auto">{{ row.replyToName }}</b>
      </p>
      <p
        v-if="row.editedAt"
        class="edit-note"
      >
        {{ row.editedByKind === 'admin' ? words.editedAdmin : words.edited }}
      </p>
      <p
        v-if="row.pinned"
        class="edit-note"
      >
        {{ words.pinned }}
      </p>
      <p
        v-if="isRichReply(row)"
        class="comment-text rich-comment-text"
        dir="auto"
        v-html="safeReplyText(row.text)"
      />
      <p
        v-else
        class="comment-text"
        dir="auto"
        v-text="row.text"
      />
      <p v-if="row.reports">
        {{ words.reports }}: {{ row.reports }}
      </p>
      <details
        v-if="canModerate"
        class="review-note"
      >
        <summary>{{ words.reason }}</summary><label :aria-label="words.reason"><input
          v-model="reasons[row['_id']]"
          :aria-label="words.reason"
          :placeholder="words.reasonPlaceholder"
          maxlength="500"
        ></label>
      </details>
      <div class="actions">
        <button
          v-if="canModerate"
          type="button"
          :disabled="busy || row.status === 'approved'"
          class="approve-action"
          @click="moderate(row, 'approved')"
        >
          <span aria-hidden="true">✓</span> {{ words.approve }}
        </button>
        <button
          v-if="canModerate"
          type="button"
          :disabled="busy || row.status === 'rejected'"
          class="reject-action"
          @click="moderate(row, 'rejected')"
        >
          <span aria-hidden="true">×</span> {{ words.reject }}
        </button>
        <button
          v-if="canReply"
          type="button"
          :disabled="busy || row.status !== 'approved'"
          class="reply-action"
          @click="
            editTarget = null
            replyTarget = row
            replyText = ''
            replySubmission = null
          "
        >
          <span aria-hidden="true">↩</span> {{ words.reply }}
        </button>
        <details
          class="row-options"
          @keydown.esc="closeOptions($event)"
        ><summary>{{ words.more }} <span aria-hidden="true">⌄</span></summary><div>
          <button
            v-if="canModerate"
            type="button"
            :disabled="busy"
            @click="startEdit(row); closeOptions($event)"
          >
            {{ words.edit }}
          </button>
          <button
            v-if="canModerate && !row.rootId"
            type="button"
            :disabled="busy || (!row.pinned && row.status !== 'approved')"
            @click="pin(row); closeOptions($event)"
          >
            {{ row.pinned ? words.unpin : words.pin }}
          </button>
          <button
            type="button"
            :disabled="busy"
            @click="conversation(row); closeOptions($event)"
          >
            {{ words.thread }}
          </button>
        </div></details>
      </div>
      <form
        v-if="editTarget && editTarget['_id'] === row['_id']"
        class="inline-editor"
        @submit.prevent="saveEdit"
      >
        <label>{{ words.text }}<textarea
          v-model="editText"
          required
          minlength="2"
          maxlength="3000"
          rows="4"
          dir="auto"
          :disabled="busy"
        /></label>
        <label>{{ words.editReason }}<input
          v-model="editReason"
          required
          minlength="2"
          maxlength="500"
          :disabled="busy"
        ></label>
        <p>{{ words.editPolicy }}</p>
        <button
          type="submit"
          :disabled="busy"
        >
          {{ words.saveEdit }}
        </button>
        <button
          type="button"
          :disabled="busy"
          @click="editTarget = null"
        >
          {{ words.cancel }}
        </button>
      </form>
      <form
        v-if="replyTarget && replyTarget['_id'] === row['_id']"
        class="inline-editor"
        @submit.prevent="sendReply"
      >
        <label>{{ words.reply
        }}<textarea
          ref="replyTextInput"
          v-model="replyText"
          :disabled="busy"
          rows="4"
          required
          minlength="2"
          maxlength="3000"
          dir="auto"
        />
        </label>
        <div class="link-actions">
          <button
            type="button"
            :disabled="busy"
            @click="openReplyLinkEditor"
          >
            {{ words.addLink }}
          </button>
          <span>{{ words.linkHint }}</span>
        </div>
        <fieldset
          v-if="replyLinkEditor.open"
          class="link-editor"
        >
          <legend>{{ words.addLink }}</legend>
          <label>{{ words.linkText }}<input
            v-model="replyLinkEditor.text"
            :disabled="busy"
            maxlength="500"
          ></label>
          <label>{{ words.linkUrl }}<input
            v-model="replyLinkEditor.url"
            :disabled="busy"
            maxlength="2000"
            dir="ltr"
            inputmode="url"
          ></label>
          <p
            v-if="replyLinkEditor.error"
            role="alert"
          >
            {{ replyLinkEditor.error }}
          </p>
          <button
            type="button"
            :disabled="busy"
            @click="applyReplyLink"
          >
            {{ words.applyLink }}
          </button>
          <button
            v-if="replyLinkEditor.current"
            type="button"
            :disabled="busy"
            @click="removeReplyLink"
          >
            {{ words.removeLink }}
          </button>
          <button
            type="button"
            :disabled="busy"
            @click="closeReplyLinkEditor"
          >
            {{ words.cancel }}
          </button>
        </fieldset>
        <div
          v-if="replyText"
          class="reply-preview"
        >
          <strong>{{ words.preview }}</strong><p
            dir="auto"
            v-html="safeReplyText(replyText)"
          />
        </div>
        <button
          type="submit"
          :disabled="busy"
        >
          {{ words.send }}
        </button>
        <button
          type="button"
          :disabled="busy"
          @click="replyTarget = null"
        >
          {{ words.cancel }}
        </button>
      </form>
    </article>
    <nav
      v-if="pages > 1"
      :aria-label="words.page"
      class="actions"
    >
      <button
        type="button"
        :disabled="loading || page <= 1"
        @click="load(page - 1)"
      >
        {{ words.previous }}
      </button>
      <span>{{ words.page }} <bdi dir="ltr">{{ page }} / {{ pages }}</bdi></span>
      <button
        type="button"
        :disabled="loading || page >= pages"
        @click="load(page + 1)"
      >
        {{ words.next }}
      </button>
    </nav>
  </section>
</template>
<script>
import axios from '@/libs/axios'
import messages from './messages'
import publicPostLink from './public-post-link'
import { buildReplyLink, replyLinkAtSelection, sanitizeReplyHtml } from './rich-reply'

export default {
  data() {
    return {
      rows: [],
      statuses: ['pending', 'approved', 'rejected'],
      status: 'pending',
      language: '',
      search: '',
      postId: '',
      rootId: '',
      page: 1,
      pages: 0,
      loading: false,
      busy: false,
      reasons: {},
      message: '',
      failed: false,
      replyTarget: null,
      replyText: '',
      replySubmission: null,
      replyLinkEditor: {
        open: false, text: '', url: '', start: 0, end: 0, current: null, error: '',
      },
      editTarget: null,
      editText: '',
      editReason: '',
      editSubmission: null,
      sequence: 0,
    }
  },
  computed: {
    showEmpty() { return !this.loading && !this.failed && !this.rows.length },
    words() {
      return messages[this.$i18n.locale] || messages.fa
    },
    canModerate() {
      return this.$can('update', 'comment')
    },
    canReply() {
      return this.$can('create', 'comment') && this.$can('update', 'comment')
    },
  },
  created() {
    this.load(1)
  },
  beforeDestroy() {
    this.sequence += 1
  },
  methods: {
    commentId(row) {
      const { _id: id } = row
      return id
    },
    closeOptions(event) { const details = event.currentTarget.closest('details'); details.open = false },
    date(value) {
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    },
    postLink(row) {
      return publicPostLink(row, process.env.VUE_APP_FRONT_BASE_URL)
    },
    postKindLabel(row) {
      return this.words[`postKind_${row.postKind}`] || this.words.post
    },
    postTitle(row) {
      const title = row.post && row.post.title
      const localized = typeof title === 'string' ? title : title && title[row.language]
      return typeof localized === 'string' && localized.trim() ? localized : this.words.postUnavailable
    },
    isRichReply(row) { return Boolean(row && row.authorKind === 'admin' && row.textFormat === 'rich') },
    safeReplyText(value) { return sanitizeReplyHtml(value) },
    openReplyLinkEditor() {
      const input = this.$refs.replyTextInput
      const source = this.replyText || ''
      const start = input && Number.isInteger(input.selectionStart) ? input.selectionStart : source.length
      const end = input && Number.isInteger(input.selectionEnd) ? input.selectionEnd : start
      const current = replyLinkAtSelection(source, start, end)
      this.replyLinkEditor = {
        open: true, text: current ? current.label : source.slice(start, end), url: current ? current.href : '', start: current ? current.start : start, end: current ? current.end : end, current, error: '',
      }
    },
    focusReplyText(position) {
      this.$nextTick(() => { const input = this.$refs.replyTextInput; if (!input) return; input.focus(); if (input.setSelectionRange) input.setSelectionRange(position, position) })
    },
    applyReplyLink() {
      const editor = this.replyLinkEditor
      const link = buildReplyLink(editor.text, editor.url)
      if (!link) { editor.error = this.words.invalidLink; return }
      const source = this.replyText || ''
      this.replyText = `${source.slice(0, editor.start)}${link}${source.slice(editor.end)}`
      const position = editor.start + link.length
      this.closeReplyLinkEditor()
      this.focusReplyText(position)
    },
    removeReplyLink() {
      const editor = this.replyLinkEditor
      if (!editor.current) return
      const source = this.replyText || ''
      this.replyText = `${source.slice(0, editor.start)}${editor.current.label}${source.slice(editor.end)}`
      const position = editor.start + editor.current.label.length
      this.closeReplyLinkEditor()
      this.focusReplyText(position)
    },
    closeReplyLinkEditor() {
      this.replyLinkEditor = {
        open: false, text: '', url: '', start: 0, end: 0, current: null, error: '',
      }
    },
    error(error) {
      const status = error.response && error.response.status
      this.failed = true
      this.message = this.words.failed
      if (status === 401) this.message = this.words.expired
      if (status === 403) this.message = this.words.forbidden
      if (status === 404) this.message = this.words.disabled
      if (status === 409) this.message = this.words.changed
    },
    async load(page) {
      this.sequence += 1
      const { sequence } = this
      this.loading = true
      if (this.failed) { this.message = ''; this.failed = false }
      try {
        const params = { page, limit: 20 };
        ['status', 'language', 'search', 'postId', 'rootId'].forEach(key => {
          if (this[key]) params[key] = this[key]
        })
        const response = await axios.get('post-comments', { params, timeout: 10000 })
        if (sequence !== this.sequence) return
        this.rows = response.data.data
        this.page = response.data.page
        this.pages = response.data.last_page
        this.reasons = {}
        this.rows.forEach(row => this.$set(this.reasons, this.commentId(row), ''))
      } catch (error) {
        if (sequence === this.sequence) this.error(error)
      } finally {
        if (sequence === this.sequence) this.loading = false
      }
    },
    conversation(row) {
      this.rootId = row.rootId || this.commentId(row)
      this.status = ''
      this.language = ''
      this.search = ''
      this.load(1)
    },
    async moderate(row, status) {
      if (this.busy) return
      this.busy = true
      this.message = ''
      try {
        await axios.patch(
          `post-comments/${this.commentId(row)}/moderation`,
          {
            status, expectedStatus: row.status, expectedVersion: row.version || 0, reason: this.reasons[this.commentId(row)] || '',
          },
          { timeout: 10000 },
        )
        this.failed = false
        this.message = this.words.saved
        await this.load(this.page)
      } catch (error) {
        this.error(error)
      } finally {
        this.busy = false
      }
    },
    startEdit(row) {
      this.replyTarget = null
      this.editTarget = row
      this.editText = row.text
      this.editReason = ''
      this.editSubmission = null
    },
    async saveEdit() {
      if (this.busy || !this.editTarget || this.editText.trim().length < 2 || this.editReason.trim().length < 2) return
      const row = this.editTarget
      const snapshot = JSON.stringify([this.commentId(row), row.version || 0, this.editText.trim(), this.editReason.trim()])
      if (!this.editSubmission || this.editSubmission.snapshot !== snapshot) {
        const bytes = new Uint8Array(16)
        window.crypto.getRandomValues(bytes)
        bytes[6] = (bytes[6] % 16) + 64
        bytes[8] = (bytes[8] % 64) + 128
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
        this.editSubmission = { snapshot, id: `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` }
      }
      this.busy = true
      try {
        await axios.patch(`post-comments/${this.commentId(row)}/admin-text`, {
          text: this.editText.trim(), expectedVersion: row.version || 0, requestId: this.editSubmission.id, reason: this.editReason.trim(),
        }, { timeout: 10000 })
        this.editTarget = null
        this.editText = ''
        this.editReason = ''
        this.editSubmission = null
        this.failed = false
        this.message = this.words.saved
        await this.load(this.page)
      } catch (error) { this.error(error) } finally { this.busy = false }
    },
    async pin(row) {
      if (this.busy) return
      this.busy = true
      try {
        await axios.patch(`post-comments/${this.commentId(row)}/pin`, { pinned: !row.pinned, expectedVersion: row.version || 0 }, { timeout: 10000 })
        this.failed = false
        this.message = this.words.saved
        await this.load(this.page)
      } catch (error) { this.error(error) } finally { this.busy = false }
    },
    async sendReply() {
      if (this.busy || this.replyText.trim().length < 2) return
      this.busy = true
      this.message = ''
      const textFormat = /<a\s+href=/i.test(this.replyText) ? 'rich' : 'plain'
      const snapshot = JSON.stringify([this.commentId(this.replyTarget), this.replyText.trim(), textFormat])
      if (!this.replySubmission || this.replySubmission.snapshot !== snapshot) {
        const bytes = new Uint8Array(16)
        window.crypto.getRandomValues(bytes)
        bytes[6] = (bytes[6] % 16) + 64
        bytes[8] = (bytes[8] % 64) + 128
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
        this.replySubmission = {
          snapshot,
          id: `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
            16,
            20,
          )}-${hex.slice(20)}`,
        }
      }
      try {
        await axios.post(
          `post-comments/posts/${this.replyTarget.postId}/replies`,
          {
            postKind: this.replyTarget.postKind || 'article',
            text: this.replyText.trim(),
            textFormat,
            parentId: this.commentId(this.replyTarget),
            publicConsent: true,
            requestId: this.replySubmission.id,
          },
          { headers: { lang: this.replyTarget.language }, timeout: 10000 },
        )
        this.replyTarget = null
        this.replyText = ''
        this.closeReplyLinkEditor()
        this.replySubmission = null
        this.failed = false
        this.message = this.words.saved
        await this.load(this.page)
      } catch (error) {
        this.error(error)
      } finally {
        this.busy = false
      }
    },
  },
}
</script>
<style scoped>
.post-moderation { --comment-surface: #fff; --comment-subtle: #f7fafa; --comment-border: #e5ecec; --comment-text: #405056; --comment-muted: #718187; color: var(--comment-text); font-family: IRANSans, Tahoma, sans-serif; font-size: 13px; text-align: start; overflow-wrap: anywhere; }
.post-moderation[dir='ltr'] { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.moderation-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 12px 0 20px; }
h2 { margin: 0; color: inherit; font-size: 20px; font-weight: 700; }
.moderation-heading p { color: var(--comment-muted); font-size: 12px; margin: 7px 0 0; }
.moderation-count { white-space: nowrap; background: var(--comment-subtle); padding: 7px 12px; border-radius: 24px; font-size: 11px; }
.filters { display: grid; grid-template-columns: 1fr 1fr 2fr 1.5fr auto; gap: 12px; align-items: end; padding: 18px; margin-bottom: 22px; border-radius: 14px; background: var(--comment-surface); border: 1px solid var(--comment-border); }
.advanced-filters summary { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; min-height: 44px; cursor: pointer; font-size: 12px; }
.advanced-filters summary span { color: #16736e; font-size: 11px; }
.advanced-filters label { margin-top: 8px; }
label { display: grid; gap: 7px; font-size: 11px; color: var(--comment-muted); }
input, select, textarea { font: inherit; font-size: 13px; border: 1px solid var(--comment-border); border-radius: 8px; padding: 10px 12px; color: var(--comment-text); background: var(--comment-surface); width: 100%; box-sizing: border-box; }
textarea { line-height: 1.9; resize: vertical; }
.moderation-row { padding: 22px; background: var(--comment-surface); border: 1px solid var(--comment-border); border-radius: 14px; margin: 14px 0; }
.official-row { border-inline-start: 3px solid #4ca8ae; background: var(--comment-subtle); }
header { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
header strong { font-size: 14px; color: inherit; }
.author-meta, time { font-size: 11px; color: var(--comment-muted); }
time { margin-inline-start: auto; }
.status-badge { font-size: 10px; padding: 4px 10px; border-radius: 20px; }
.status-pending { background: #fff3df; color: #865a16; }
.status-approved { background: #e6f5ef; color: #256b51; }
.status-rejected { background: #fcecef; color: #963f50; }
.post-context { background: var(--comment-subtle); padding: 9px 12px; border-radius: 8px; font-size: 12px; margin: 14px 0 8px; }
.post-context { overflow-wrap: anywhere; }
.post-reference .post-slug { display: block; overflow-wrap: anywhere; margin-top: 4px; }
.post-reference { color: var(--comment-muted); font-size: 10px; }
.post-reference summary { display: inline; }
.comment-text { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 2; font-size: 14px; margin: 16px 0; }
.edit-note { font-size: 10px; color: var(--comment-muted); margin: 8px 0; }
.review-note { font-size: 11px; color: var(--comment-muted); margin-top: 14px; }
.review-note input { margin-top: 10px; }
summary { cursor: pointer; }
.actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin: 16px 0 0; }
button { font: inherit; font-size: 12px; min-height: 40px; cursor: pointer; border-radius: 8px; border: 1px solid var(--comment-border); background: var(--comment-surface); color: var(--comment-text); padding: 8px 14px; }
button:hover { background: var(--comment-subtle); }
.approve-action, .filters button[type='submit'] { background: #16757c; color: white; border-color: #16757c; }
.approve-action:hover { background: #125f65; }
.reject-action { color: #963f50; }
.reply-action { color: #176f76; background: #edf7f7; border-color: #edf7f7; }
.row-options { position: relative; margin-inline-start: auto; font-size: 11px; }
.row-options summary { list-style: none; padding: 10px; min-height: 40px; box-sizing: border-box; }
.row-options summary::-webkit-details-marker { display: none; }
.row-options div { position: absolute; inset-inline-end: 0; top: 42px; width: 170px; z-index: 2; background: var(--comment-surface); box-shadow: 0 4px 24px #263d3d18; border: 1px solid var(--comment-border); border-radius: 10px; padding: 5px; }
.row-options button { display: block; width: 100%; border: 0; text-align: start; }
.inline-editor { display: grid; gap: 12px; border-top: 1px solid var(--comment-border); margin-top: 20px; padding-top: 20px; }
.inline-editor p { font-size: 11px; color: var(--comment-muted); line-height: 1.9; }
.link-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 9px; font-size: 11px; color: var(--comment-muted); }
.link-editor, .reply-preview { border: 1px solid var(--comment-border); border-radius: 10px; padding: 12px; background: var(--comment-subtle); }
.link-editor { display: grid; gap: 10px; }
.link-editor legend { padding: 0 5px; color: var(--comment-muted); font-size: 11px; }
.reply-preview p { margin: 7px 0 0; color: var(--comment-text); }
.rich-comment-text a, .reply-preview a { color: #087b86; text-decoration: underline; text-underline-offset: .18em; }
button:disabled { opacity: .45; cursor: not-allowed; }
:focus-visible { outline: 2px solid #1d9199; outline-offset: 3px; }
@media(max-width:900px) { .filters { grid-template-columns: 1fr 1fr; } }
@media(max-width:600px) { .moderation-row { padding: 15px; } .filters { padding: 14px; gap: 10px; } .filters label:nth-child(3), .filters label:nth-child(4) { grid-column: 1 / -1; } .moderation-heading { align-items: start; } .moderation-count { display: none; } time { margin-inline-start: 0; } }
</style>
<style>
.dark-layout .post-moderation { --comment-surface: #283046; --comment-subtle: #222a3e; --comment-border: #3b455b; --comment-text: #d0d5e0; --comment-muted: #a6b0c2; }
</style>

<style scoped>
.post-link { display: inline-flex; flex-wrap: wrap; align-items: baseline; gap: .4rem; color: #087b86; text-decoration: underline; text-underline-offset: .18em; overflow-wrap: anywhere; }
.post-link:focus-visible { outline: 3px solid #087b86; outline-offset: 4px; border-radius: 3px; }
.post-link-hint { display: inline-block; margin-inline-start: .4rem; font-size: .85em; font-weight: 400; }
</style>
